import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

async function fetchDefectSeverityIndex(queryString) {
  const url = `${QUALITY_SERVICE_API_URL}/hub/defect-severity-index?${queryString}`;
  try {
    const response = await api.get(url);
    return response.data;
  } catch (err) {
    console.error('[useDefectSeverityIndex] fetch failed:', {
      status: err.response?.status,
      message: err.message,
    });
    throw err;
  }
}

function useDefectSeverityIndex(filters, dateRange) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build query string
  const params = new URLSearchParams();
  ['domain', 'group', 'unit'].forEach(key => {
    (filters?.[key] || []).forEach(value => params.append(key, value));
  });

  const formatDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  if (dateRange?.startDate) params.set('from', formatDate(dateRange.startDate));
  if (dateRange?.endDate) params.set('to', formatDate(dateRange.endDate));

  const queryString = params.toString();

  const fetchData = useCallback(async (isCancelled = () => false) => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    try {
      const dsiData = await fetchDefectSeverityIndex(queryString);
      if (!isCancelled()) setData(dsiData);
    } catch (err) {
      if (!isCancelled()) setError(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [queryString, dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    let cancelled = false;
    fetchData(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchData]);

  return { data, loading, error, refetch: () => fetchData() };
}

export default useDefectSeverityIndex;
